// Couche de données Facturation (Super Admin) — source de vérité : Supabase.
//
// Les abonnements proviennent de la table `abonnements` et les paiements de la
// table `paiements_abonnements`. Le Super Admin y accède via les politiques RLS
// déjà en place. Les formules sont lues dans `formules` (prix et limites réels).
//
// Aucune donnée fictive : les collections reflètent uniquement ce qui existe en
// base. Les colonnes absentes du schéma (renouvellement, période réglée) ne sont
// pas inventées — elles sont laissées à leurs valeurs neutres.

import { supabase } from "@/store/shared/supabase";
import { createUuid } from "@/store/shared/id";
import type { FormuleId, Periodicite } from "@/config/formules";
import { PAIEMENT_METHODE_CODES } from "@/config/paiement";
import {
  type AbonnementStatut,
  type EmailValidationStatut,
  type PaiementMethode,
  type PaiementStatut,
  type PlatformAbonnement,
  type PlatformAbonnementInput,
  type PlatformPaiement,
  type PlatformPaiementInput,
} from "@/store/billing/types";

type Row = Record<string, unknown>;
const str = (value: unknown): string => (typeof value === "string" ? value : "");
const num = (value: unknown): number => (typeof value === "number" ? value : Number(value ?? 0) || 0);
const rows = (value: unknown): Row[] => (Array.isArray(value) ? (value as Row[]) : []);

const ABONNEMENT_STATUTS: AbonnementStatut[] = ["actif", "en_attente", "expire", "suspendu", "annule"];
// Valeurs autorisées par les contraintes CHECK de `paiements_abonnements`.
const PAIEMENT_STATUTS: PaiementStatut[] = ["en_attente", "valide", "rejete", "annule"];
// Moyens STRICTEMENT limités aux 3 moyens locaux autorisés (source : @/config/paiement).
const PAIEMENT_METHODES: PaiementMethode[] = PAIEMENT_METHODE_CODES;

function abonnementStatutFromDb(value: string): AbonnementStatut {
  return (ABONNEMENT_STATUTS as string[]).includes(value) ? (value as AbonnementStatut) : "en_attente";
}

function paiementStatutFromDb(value: string): PaiementStatut {
  return (PAIEMENT_STATUTS as string[]).includes(value) ? (value as PaiementStatut) : "en_attente";
}

function paiementMethodeFromDb(value: string): PaiementMethode {
  // Tout moyen hérité hors des 3 autorisés est ramené sur un libellé sûr
  // (aucune création de nouveau moyen ne peut se produire côté application).
  return (PAIEMENT_METHODES as string[]).includes(value) ? (value as PaiementMethode) : "especes";
}

const EMAIL_VALIDATION_STATUTS: EmailValidationStatut[] = ["non_envoye", "en_cours", "envoye", "echec"];

function emailStatutFromDb(value: string): EmailValidationStatut {
  return (EMAIL_VALIDATION_STATUTS as string[]).includes(value) ? (value as EmailValidationStatut) : "non_envoye";
}

export function periodiciteToDb(value: Periodicite): string {
  return value === "annuel" ? "annuelle" : "mensuelle";
}

export function periodiciteFromDb(value: string): Periodicite {
  return value === "annuelle" ? "annuel" : "mensuel";
}

function typeFromRole(role: string): string {
  if (role === "dg_proprietaire") return "proprietaire";
  if (role === "dg_agence") return "agence";
  return "utilisateur";
}

function computeDateFin(debut: string, periodicite: Periodicite): string {
  const base = debut ? new Date(debut) : new Date();
  if (Number.isNaN(base.getTime())) return "";
  const date = new Date(base);
  if (periodicite === "annuel") date.setFullYear(date.getFullYear() + 1);
  else date.setMonth(date.getMonth() + 1);
  return date.toISOString().slice(0, 10);
}

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

export interface BillingSnapshot {
  abonnements: PlatformAbonnement[];
  paiements: PlatformPaiement[];
}

export async function fetchBillingData(): Promise<BillingSnapshot> {
  const [aboRes, payRes, formulesRes, proprietairesRes, agencesRes, comptesRes] = await Promise.all([
    supabase.from("abonnements").select("*").order("created_at", { ascending: false }),
    supabase.from("paiements_abonnements").select("*").order("date_paiement", { ascending: false }),
    supabase.from("formules").select("id, code"),
    supabase.from("proprietaires").select("id, compte_id, nom_complet"),
    supabase.from("agences").select("id, compte_id, nom"),
    supabase.from("comptes").select("id, nom_complet, type_compte, role"),
  ]);

  const firstError = [aboRes, payRes, formulesRes, proprietairesRes, agencesRes, comptesRes]
    .map((result) => result.error)
    .find(Boolean);
  if (firstError) throw firstError;

  const aboRows = rows(aboRes.data);
  const payRows = rows(payRes.data);
  const formuleRows = rows(formulesRes.data);
  const proprioRows = rows(proprietairesRes.data);
  const agenceRows = rows(agencesRes.data);
  const compteRows = rows(comptesRes.data);

  const formuleCodeById = new Map(formuleRows.map((row) => [str(row.id), str(row.code)]));
  const proprioById = new Map(proprioRows.map((row) => [str(row.id), row]));
  const agenceById = new Map(agenceRows.map((row) => [str(row.id), row]));
  const compteById = new Map(compteRows.map((row) => [str(row.id), row]));

  const abonnements: PlatformAbonnement[] = aboRows.map((row) => {
    const proprioId = str(row.proprietaire_id);
    const agenceId = str(row.agence_id);
    let compteId = "";
    let compteNom = "";
    let compteType = "";

    if (proprioId) {
      const proprio = proprioById.get(proprioId);
      compteId = proprio ? str(proprio.compte_id) : "";
      compteNom = proprio ? str(proprio.nom_complet) : "";
      compteType = "proprietaire";
    } else if (agenceId) {
      const agence = agenceById.get(agenceId);
      compteId = agence ? str(agence.compte_id) : "";
      compteNom = agence ? str(agence.nom) : "";
      compteType = "agence";
    }

    if (compteId) {
      const compte = compteById.get(compteId);
      if (compte) {
        compteNom = str(compte.nom_complet) || compteNom;
        compteType = typeFromRole(str(compte.role)) || compteType;
      }
    }

    return {
      id: str(row.id),
      compteId,
      compteNom,
      compteType,
      formuleId: formuleCodeById.get(str(row.formule_id)) ?? "",
      periodicite: periodiciteFromDb(str(row.periodicite)),
      montant: num(row.montant),
      statut: abonnementStatutFromDb(str(row.statut)),
      dateDebut: str(row.date_debut),
      dateFin: str(row.date_fin),
      renouvellement: false,
      createdAt: str(row.created_at),
    };
  });

  const abonnementById = new Map(abonnements.map((abonnement) => [abonnement.id, abonnement]));

  const paiements: PlatformPaiement[] = payRows.map((row) => {
    const abonnement = abonnementById.get(str(row.abonnement_id));
    const datePaiement = str(row.date_paiement);
    return {
      id: str(row.id),
      abonnementId: str(row.abonnement_id),
      compteId: abonnement?.compteId ?? "",
      compteNom: abonnement?.compteNom ?? "",
      formuleId: abonnement?.formuleId ?? "",
      montant: num(row.montant),
      date: datePaiement ? datePaiement.slice(0, 10) : "",
      methode: paiementMethodeFromDb(str(row.mode_paiement)),
      reference: str(row.reference),
      statut: paiementStatutFromDb(str(row.statut)),
      periode: "",
      preuveUrl: str(row.preuve_url),
      commentaire: str(row.commentaire),
      createdAt: datePaiement,
      emailStatut: emailStatutFromDb(str(row.email_validation_statut)),
      emailErreur: str(row.email_validation_erreur),
    };
  });

  return { abonnements, paiements };
}

/* ------------------------------------------------------------------ */
/* Écritures                                                           */
/* ------------------------------------------------------------------ */

async function resolveFormuleId(code: FormuleId | string): Promise<string> {
  if (!code) throw new Error("Aucune formule sélectionnée.");
  const { data, error } = await supabase.from("formules").select("id").eq("code", code).maybeSingle();
  if (error) throw error;
  const id = (data as { id?: string } | null)?.id;
  if (!id) throw new Error(`Formule introuvable : ${code}`);
  return id;
}

async function resolveOwner(compteId: string, compteType: string): Promise<{ proprietaire_id: string | null; agence_id: string | null }> {
  if (!compteId) throw new Error("Aucun compte sélectionné.");
  if (compteType === "agence") {
    const { data, error } = await supabase.from("agences").select("id").eq("compte_id", compteId).maybeSingle();
    if (error) throw error;
    const id = (data as { id?: string } | null)?.id;
    if (!id) throw new Error("Agence introuvable pour ce compte.");
    return { proprietaire_id: null, agence_id: id };
  }
  const { data, error } = await supabase.from("proprietaires").select("id").eq("compte_id", compteId).maybeSingle();
  if (error) throw error;
  const id = (data as { id?: string } | null)?.id;
  if (!id) throw new Error("Propriétaire introuvable pour ce compte.");
  return { proprietaire_id: id, agence_id: null };
}

export async function insertAbonnementRecord(input: PlatformAbonnementInput): Promise<void> {
  const formuleId = await resolveFormuleId(input.formuleId);
  const owner = await resolveOwner(input.compteId, input.compteType);
  const debut = input.dateDebut || todayISO();
  const fin = input.dateFin || computeDateFin(debut, input.periodicite);
  const { error } = await supabase.from("abonnements").insert({
    ...owner,
    formule_id: formuleId,
    periodicite: periodiciteToDb(input.periodicite),
    statut: input.statut,
    date_debut: debut,
    date_fin: fin,
    montant: input.montant,
  });
  if (error) throw error;
}

export async function updateAbonnementRecord(id: string, input: PlatformAbonnementInput): Promise<void> {
  const formuleId = await resolveFormuleId(input.formuleId);
  const debut = input.dateDebut || todayISO();
  const fin = input.dateFin || computeDateFin(debut, input.periodicite);
  const { error } = await supabase
    .from("abonnements")
    .update({
      formule_id: formuleId,
      periodicite: periodiciteToDb(input.periodicite),
      statut: input.statut,
      date_debut: debut,
      date_fin: fin,
      montant: input.montant,
    })
    .eq("id", id);
  if (error) throw error;
}

export async function deleteAbonnementRecord(id: string): Promise<void> {
  // Écriture réservée au Super Admin : passe par une fonction serveur sécurisée
  // (SECURITY DEFINER) qui revérifie is_super_admin() et journalise la suppression.
  const { error } = await supabase.rpc("super_admin_supprimer_abonnement", { p_abonnement_id: id });
  if (error) throw error;
}

export async function updateAbonnementStatutRecord(id: string, statut: AbonnementStatut): Promise<void> {
  const { error } = await supabase.from("abonnements").update({ statut }).eq("id", id);
  if (error) throw error;
}

export async function insertPaiementRecord(input: PlatformPaiementInput): Promise<void> {
  if (!input.abonnementId) throw new Error("Aucun abonnement sélectionné.");
  const { error } = await supabase.from("paiements_abonnements").insert({
    abonnement_id: input.abonnementId,
    montant: input.montant,
    mode_paiement: input.methode,
    reference: input.reference || null,
    preuve_url: input.preuveUrl || null,
    commentaire: input.commentaire || null,
    statut: input.statut,
    date_paiement: input.date ? new Date(`${input.date}T00:00:00Z`).toISOString() : new Date().toISOString(),
  });
  if (error) throw error;
}

export async function updatePaiementRecord(id: string, input: PlatformPaiementInput): Promise<void> {
  // Écriture réservée au Super Admin : passe par une fonction serveur sécurisée
  // (SECURITY DEFINER) qui revérifie is_super_admin(), valide statut/mode et journalise.
  const { error } = await supabase.rpc("super_admin_modifier_paiement_abonnement", {
    p_paiement_id: id,
    p_montant: input.montant,
    p_mode_paiement: input.methode,
    p_reference: input.reference || null,
    p_preuve_url: input.preuveUrl || null,
    p_commentaire: input.commentaire || null,
    p_statut: input.statut,
    p_date_paiement: input.date ? new Date(`${input.date}T00:00:00Z`).toISOString() : null,
  });
  if (error) throw error;
}

export async function deletePaiementRecord(id: string): Promise<void> {
  // Écriture réservée au Super Admin : passe par une fonction serveur sécurisée
  // (SECURITY DEFINER) qui revérifie is_super_admin() et journalise la suppression.
  const { error } = await supabase.rpc("super_admin_supprimer_paiement_abonnement", { p_paiement_id: id });
  if (error) throw error;
}

export async function updatePaiementStatutRecord(id: string, statut: PaiementStatut): Promise<void> {
  const { error } = await supabase.from("paiements_abonnements").update({ statut }).eq("id", id);
  if (error) throw error;
}

/** Identifiant temporaire pour les mises à jour optimistes (remplacé après rechargement). */
export function tempId(): string {
  return createUuid();
}